import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Users } from "lucide-react";
import SeoMeta from "../components/SeoMeta";
import { SiteNav } from "../components/home/SiteNav";
import { SiteFooter } from "../components/home/BreathworkAndFooter";
import { Reveal, Eyebrow } from "../components/home/shared";
import { PageHero } from "../components/site/Sections";

// Booking buttons point at the existing accommodation pages until feralstays.com is ready.
const ESTATES = [
  {
    key: "bron-menai", name: "Bron Menai", place: "Newborough",
    copy: "A fully renovated country manor and cottages set within eight acres of private grounds, four miles from Newborough beach and forest. Sauna, cold plunge, hot tub and a walled garden studio.",
    sleeps: "Sleeps up to 23.", image: "/bron-exterior.jpeg", detail: "/home/hottub.webp", to: "/feral-stays/bron-menai",
  },
  {
    key: "plas-llechylched", name: "Plas Llechylched", place: "Rhosneigr",
    copy: "A historic 19th-century courtyard estate of seven cottages, minutes from the wild beaches of Rhosneigr. Log fires, hot tubs, fire pits and cinematic sunsets.",
    sleeps: "Sleeps up to 36.", image: "/plas-exterior.jpeg", detail: "/plas-interior.jpg", to: "/feral-stays/plas-llechylched",
  },
  {
    key: "hotel-cymyran", name: "Hotel Cymyran", place: "Anglesey",
    copy: "Five houses on Anglesey's west coast, perfect for groups or as a base for exploring the island. The main hotel opens in late 2027.",
    sleeps: "Sleeps up to 36.", image: "/cymyran-exterior.png", detail: "/home/shore.webp", href: "mailto:hello@feralcollective.co.uk?subject=Hotel%20Cymyran%20stay%20enquiry",
  },
];

const BookBtn = ({ e }) => {
  const cls = "inline-flex items-center gap-2 feral-header text-xs font-bold tracking-[0.2em] bg-[#9b3d0a] text-[#f2e5de] px-7 py-4 hover:bg-[#2c6052] transition-colors duration-300";
  return e.to
    ? <Link to={e.to} className={cls} data-testid={`stays-book-${e.key}`}>Explore & Book <ArrowRight size={14} /></Link>
    : <a href={e.href} className={cls} data-testid={`stays-book-${e.key}`}>Explore & Book <ArrowRight size={14} /></a>;
};

const Estate = ({ e, i }) => (
  <article className={`grid md:grid-cols-12 gap-8 md:gap-12 items-center ${i ? "mt-28 md:mt-40" : ""}`} data-testid={`stays-estate-${e.key}`}>
    <Reveal className={`md:col-span-7 relative ${i % 2 ? "md:order-2" : ""}`}>
      <div className="overflow-hidden aspect-[4/3]"><img src={e.image} alt={`${e.name}, ${e.place}`} loading={i ? "lazy" : "eager"} className="w-full h-full object-cover transition-transform duration-1000 hover:scale-[1.04]" /></div>
      <img src={e.detail} alt="" loading="lazy" className={`hidden lg:block absolute -bottom-14 w-[34%] aspect-[3/4] object-cover border-[10px] border-[#f2e5de] ${i % 2 ? "-left-10" : "-right-10"}`} />
    </Reveal>
    <Reveal className={`md:col-span-5 ${i % 2 ? "md:order-1" : ""}`} delay={0.1}>
      <p className="feral-serif text-[#9b3d0a] text-2xl">0{i + 1}</p>
      <h2 className="feral-serif text-[#9b3d0a] text-5xl lg:text-6xl leading-[0.95] tracking-tight mt-3">{e.name}</h2>
      <Eyebrow className="text-[#2c6052] mt-4">{e.place}</Eyebrow>
      <p className="mt-6 text-lg font-light leading-relaxed text-[#231f20]/80">{e.copy}</p>
      <p className="mt-5 flex items-center gap-2 text-[#2c6052] font-semibold"><Users size={16} /> {e.sleeps}</p>
      <div className="mt-8"><BookBtn e={e} /></div>
    </Reveal>
  </article>
);

export default function Stays() {
  return (
    <div className="bg-[#f2e5de]" data-testid="stays-page">
      <SeoMeta title="Holiday cottages and estates in Anglesey | FERAL Stays" description="Three historic Welsh estates on the wild edges of Anglesey. Cottages, manor houses and room to breathe: Bron Menai, Plas Llechylched and Hotel Cymyran." canonicalPath="/stays" image="/bron-exterior.jpeg" />
      <SiteNav />
      <PageHero testid="stays-hero" eyebrow="FERAL Stays" title="Stay with us." image="/plas-exterior.jpeg"
        copy="Three historic Welsh estates on the wild edges of Anglesey. Cottages, manor houses and room to breathe." />
      <section className="feral-canvas py-24 md:py-36 px-6 md:px-10 overflow-x-clip">
        <div className="max-w-[1400px] mx-auto">
          {ESTATES.map((e, i) => <Estate key={e.key} e={e} i={i} />)}
        </div>
      </section>
      <SiteFooter />
    </div>
  );
}
